import React, { useState, useEffect, useCallback } from 'react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { Card } from '@/components/ui/card';
import { Building2, Users, TrendingUp, AlertTriangle, Clock, CreditCard, Zap, PauseCircle } from 'lucide-react';
import { API } from '@/utils/api';
import axios from 'axios';

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <Card className="bg-slate-900 border-slate-800 p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color || 'text-white'}`}>{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </div>
      <div className={`p-2.5 rounded-lg ${color ? color.replace('text-', 'bg-') + '/10' : 'bg-slate-800'}`}>
        <Icon className={`w-5 h-5 ${color || 'text-slate-400'}`} />
      </div>
    </div>
  </Card>
);

const SuperAdminDashboard = ({ auth, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/saas/dashboard/stats`),
        axios.get(`${API}/saas/logs?limit=10`),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const formatCHF = (v) => `CHF ${(v || 0).toLocaleString('fr-CH', { minimumFractionDigits: 0 })}`;
  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const actionLabels = {
    creation: 'Création',
    changement_plan: 'Changement plan',
    changement_statut: 'Changement statut',
    paiement: 'Paiement',
    suspension: 'Suspension',
    reactivation: 'Réactivation',
    suppression: 'Suppression',
  };

  const actionColors = {
    creation: 'text-emerald-400',
    paiement: 'text-blue-400',
    suspension: 'text-red-400',
    reactivation: 'text-emerald-400',
    suppression: 'text-red-400',
    changement_plan: 'text-amber-400',
    changement_statut: 'text-amber-400',
  };

  return (
    <SuperAdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 lg:p-8 space-y-8" data-testid="superadmin-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-white">Tableau de bord SaaS</h1>
          <p className="text-sm text-slate-400 mt-1">Vue d'ensemble de la plateforme Vortekio</p>
        </div>

        {loading ? (
          <div className="text-slate-400 text-center py-20">Chargement...</div>
        ) : stats && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Building2} label="Total entreprises" value={stats.total_tenants} color="text-white" />
              <StatCard icon={Zap} label="Actives" value={stats.active} color="text-emerald-400" />
              <StatCard icon={Clock} label="En essai" value={stats.trial} color="text-blue-400" />
              <StatCard icon={PauseCircle} label="Suspendues" value={stats.suspended} color="text-red-400" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <StatCard icon={CreditCard} label="Revenus ce mois" value={formatCHF(stats.monthly_revenue)} color="text-emerald-400" />
              <StatCard icon={TrendingUp} label="Revenus totaux" value={formatCHF(stats.total_revenue)} color="text-blue-400" />
              <StatCard icon={AlertTriangle} label="Expirent bientôt" value={stats.expiring_soon} color="text-amber-400" sub="Dans les 30 prochains jours" />
            </div>

            {/* Plan breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-900 border-slate-800 p-6">
                <h2 className="text-base font-semibold text-white mb-4">Répartition par plan</h2>
                <div className="space-y-3">
                  {Object.entries(stats.plan_counts || {}).map(([plan, count]) => {
                    const planLabels = { trial: 'Essai', starter: 'Starter', business: 'Business', enterprise: 'Enterprise', enterprise_plus: 'Enterprise+' };
                    const planColors = { trial: 'bg-slate-600', starter: 'bg-blue-600', business: 'bg-indigo-600', enterprise: 'bg-purple-600', enterprise_plus: 'bg-amber-600' };
                    const total = stats.total_tenants || 1;
                    return (
                      <div key={plan} className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${planColors[plan] || 'bg-slate-500'}`} />
                        <span className="text-sm text-slate-300 flex-1">{planLabels[plan] || plan}</span>
                        <span className="text-sm font-medium text-white">{count}</span>
                        <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${planColors[plan] || 'bg-slate-500'}`} style={{ width: `${(count / total) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(stats.plan_counts || {}).length === 0 && (
                    <p className="text-sm text-slate-500">Aucune entreprise</p>
                  )}
                </div>
              </Card>

              {/* Recent activity */}
              <Card className="bg-slate-900 border-slate-800 p-6">
                <h2 className="text-base font-semibold text-white mb-4">Activité récente</h2>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {logs.length === 0 ? (
                    <p className="text-sm text-slate-500">Aucune activité</p>
                  ) : logs.map((log) => (
                    <div key={log.id} className="flex items-start gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${(actionColors[log.action] || 'text-slate-400').replace('text-', 'bg-')}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-300 truncate">
                          <span className={`font-medium ${actionColors[log.action] || 'text-slate-300'}`}>{actionLabels[log.action] || log.action}</span>
                          {log.company_name && <span className="text-slate-500"> — {log.company_name}</span>}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{log.detail}</p>
                      </div>
                      <span className="text-xs text-slate-600 shrink-0">{formatDate(log.created_at)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
